<template>
  <div
    ref="selectRef"
    class="select"
    :class="baseSelectClass"
    tabindex="0"
    v-click-outside="onClickOutside"
  >
    <span
      v-if="styled && label"
      class="select__label"
      @click.stop="onToggleSelect"
    >
      {{ label }}
    </span>
    <div class="select-field">
      <div
        class="select-field__control"
        :class="{ focused: isOpen }"
        @click="onToggleSelect"
      >
        <div class="select-field__prepend">
          <slot name="prepend"></slot>
        </div>
        <div class="select-field__native">
          <input
            ref="inputRef"
            :value="inputValue"
            class="select-field__input"
            :class="[{ 'use-input': useInput && styled }]"
            type="text"
            :placeholder="placeholder"
            :readonly="!useInput"
            @input="$emit('update:inputValue', $event?.target?.value)"
          />
          <span
            v-if="!useInput"
            class="select-field__value"
            :class="[{ link: link }, { 'upper-case': upperCase }]"
          >
            <slot name="value">
              {{ modelValue?.value ? modelValue?.value : "Выбрать" }}
            </slot>
          </span>
        </div>
        <div v-if="arrow && !useInput" class="select-field__append">
          <BaseIcon
            icon="arrow"
            class="select-field__arrow"
            :class="{ 'select-field__arrow_up': isOpen }"
          />
        </div>
      </div>
      <Transition name="select">
        <div
          v-if="isOpen"
          class="select-options"
          :class="optionsStyles">
          <div v-if="searchable" class="search-input-wrapper">
            <slot name="search"></slot>
          </div>
          <slot name="select-tab"></slot>
          <template v-if="options.length">
            <slot name="first-option"></slot>
            <div
              v-for="option in options"
              :key="`select-${option?.id}`"
              class="select-option"
              :class="{
                'select-option_selected': selectedOptionClass(option),
                'select-option_disabled ': (option?.isAvailable === false || isDisabledAllOptions),
              }"
              @click="changeOption(option)"
              v-tooltip.top="
                isTooltip &&
                  checkOptionWidthForShowingTooltip(option.id) &&
                  $t(option.id)
              "
              :pt="{
                root: 'p-tooltip',
                text: 'p-tooltip-text',
                arrow: 'p-tooltip-arrow'
              }"
            >
              <div
                v-if="radio"
                class="select-option__checkbox circle-checkbox"
                :class="{
                  'circle-checkbox_checked': selectedOptionClass(option),
                }"
              >
                <div></div>
              </div>

              <div
                v-if="checkbox"
                class="select-option__checkbox checkbox"
                :class="{
                  checkbox_checked: selectedOptionClass(option),
                }"
              ></div>

              <div class="select-option__value">
                <div
                  class="select-option__value-text"
                  :class="[{ 'upper-case': upperCase }]"
                >
                  <div
                    class="select-option__text"
                    :id="option.id"
                    ref="optionText"
                  >
                    <slot name="option" :option="option">
                      <span>{{ option.value }}</span>
                    </slot>
                  </div>
                  <div
                    v-if="!option.isAvailable && option.reason"
                    class="select-option-info"
                    v-tooltip.bottom="isTooltip && option.reason"
                  >
                    <BaseIcon class="select-option-info__icon" icon="info" />
                  </div>
                </div>
                <span v-if="option.details && styled">
                  {{ option.details }}
                </span>
              </div>
            </div>
          </template>
          <template v-else-if="!options.length && searchable">
            <div class="select-option">
              <span>{{ $t("No option") }}</span>
            </div>
          </template>
          <template v-else>
            <div class="select-option">
              <SpinnerRadial v-if="isLoadSearch" size="30" />
              <span v-else>{{ $t("No option") }}</span>
            </div>
          </template>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from "vue";

import { ref, computed } from "vue";
import BaseIcon from "~/components/icons/BaseIcon.vue";
import BaseCheckbox from "~/components/input/BaseCheckbox.vue";
import SpinnerRadial from "~/components/loaders/SpinnerRadial.vue";

export type BaseSelectOption = {
  value: string;
  id: string;
  isAvailable?: boolean;
  reason?: string;
  path?: string;
  details?: string | null;
};

export default defineNuxtComponent({
  components: { SpinnerRadial, BaseCheckbox, BaseIcon },
  props: {
    modelValue: {
      type: [Object, Array] as PropType<BaseSelectOption | BaseSelectOption[]>,
      default: null,
    },
    options: {
      type: Array as PropType<BaseSelectOption[]>,
      default: [],
    },
    isDisabledAllOptions: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    inputValue: {
      type: String as PropType<string>,
    },
    inputSearchValue: {
      type: String as PropType<string>,
      default: "",
    },
    useInput: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    multiple: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    upperCase: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    searchable: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    checkbox: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    radio: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    arrow: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    label: {
      type: String as PropType<string>,
      default: null,
    },
    icon: {
      type: String as PropType<string>,
      default: null,
    },
    listPositionTop: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    fullHeight: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    link: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    styled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    isLoadSearch: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    placeholder: {
      type: String as PropType<string | undefined>,
      default: null,
    },
    isTooltip: {
      type: Boolean,
      default: null,
    },
    clearSearch: {
      type: Function as PropType<() => void>,
      required: false,
    },
  },
  emits: ["change", "update:inputValue"],

  setup(props, { emit }) {
    const selectRef = ref<HTMLElement | null>(null);
    const inputRef = ref<HTMLElement | null>(null);
    const isOpen = ref(false);
    const optionText = ref<HTMLDivElement[] | []>([]);

    const onToggleSelect = () => {
      if (props.useInput) {
        inputRef.value?.focus();
      }
      isOpen.value = !isOpen.value;
    };

    const onClickOutside = () => {
      isOpen.value = false;
      if (props.clearSearch) {
        props.clearSearch();
      }
    };

    watch(
      () => props.inputValue,
      () => {
        if (inputRef.value === document.activeElement) {
          isOpen.value = true;
        }
      }
    );

    watch(
      () => isOpen.value,
      async () => {
        if (props.inputValue === "") {
          changeOption({ id: "", value: "" });
        }
      }
    );

    const checkOptionWidthForShowingTooltip = (
      id: string
    ): boolean | undefined => {
      if (!optionText.value.length) {
        return;
      }

      const element = optionText.value.find((option) => option.id === id);

      if (!element) {
        return;
      }

      return element.scrollWidth > element.offsetWidth;
    };

    const optionsStyles = computed(() => ({
      top: props.listPositionTop,
      "full-height": props.fullHeight
    }));
    const baseSelectClass = computed(() => ({
      "styled-select": props.styled,
      "use-input": props.useInput,
    }));
    const selectedOptionClass = (option: BaseSelectOption): boolean => {
      if (Array.isArray(props.modelValue)) {
        return props.modelValue.some((e) => e?.id === option?.id);
      } else {
        return props.modelValue?.id === option.id;
      }
    };

    const changeOption = (value: BaseSelectOption) => {
      if (value?.isAvailable !== false && !props.isDisabledAllOptions) {
        emit("change", value);
        if (!props.multiple && !!selectRef.value) {
          isOpen.value = false;
        }
      }
    };

    return {
      isOpen,
      baseSelectClass,
      selectRef,
      inputRef,
      optionsStyles,
      selectedOptionClass,
      onToggleSelect,
      changeOption,
      onClickOutside,
      optionText,
      checkOptionWidthForShowingTooltip,
    };
  },
});
</script>
